/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment'
import { Location, LocationRetailer } from './location.model';
import { SessionStorageService } from '../services/session-storage.service';
import { APIResponseWrapper } from '../models/common/api-response-wrapper';
import { APIListWrapper } from "../models/common/api-list-wrapper.model";
import { Observable, of, finalize, shareReplay } from "rxjs";

export interface LocationDirectoryOption {
    countryId: number | null;
    cityId: number | null;
    retailerId: number | null;
    retailerName: string | null;
    zoneId: number | null;
}
export interface LocationDirectoryOptions {
    options: LocationDirectoryOption[];
    total: number;
    warehouses: number;
    customers: number;
}

@Injectable({
    providedIn: 'root'
})
export class LocationService {

    restApiUrl: string
    private readonly comboRequests = new Map<string, Observable<APIListWrapper<Location>>>();

    constructor(private http: HttpClient, private sessionStorageService: SessionStorageService) {
        this.restApiUrl = `${environment.restApiUrl}`;
    }

    getLocationById(locationId: number) {
        return this.http.get<APIResponseWrapper<Location[]>>(`${this.restApiUrl}location-endpoint/locations?locationId=${locationId}&cultureCode=en-US`);
    }

    getLocationsByManagedBy(managedBy: number) {
        return this.http.get<APIResponseWrapper<Location[]>>(`${this.restApiUrl}location-endpoint/locations?managedBy=${managedBy}&cultureCode=en-US`);
    }

    getDirectory(companyId: number, page: number, pageSize: number, filters: Record<string, string | number | null>) {
        const params: Record<string, string | number> = { companyId, page, pageSize };
        Object.entries(filters).forEach(([key, value]) => {
            if (value != null && String(value).trim()) params[key] = value;
        });
        return this.http.get<APIListWrapper<Location>>(`${this.restApiUrl}location-endpoint/directory`, { params });
    }

    getDirectoryOptions(companyId: number) {
        return this.http.get<LocationDirectoryOptions>(`${this.restApiUrl}location-endpoint/directory-options`,
            { params: { companyId } });
    }

    getLocationsByCompanyId(page: number, pageSize: number, companyId: number) {
        return this.http.get<APIListWrapper<Location>>(`${this.restApiUrl}location-endpoint/locations-by-company?page=${page}&pageSize=${pageSize}&companyId=${companyId}&cultureCode=en-US`);
    }

    getRetailers(owningCompanyId: number, includeInactive = false) {
        return this.http.get<APIResponseWrapper<LocationRetailer[]>>(
            `${this.restApiUrl}location-endpoint/retailers`,
            { params: { owningCompanyId, includeInactive } }
        );
    }

    createRetailer(retailer: LocationRetailer) {
        return this.http.post<APIResponseWrapper<LocationRetailer>>(
            `${this.restApiUrl}location-endpoint/retailers`, retailer
        );
    }

    updateRetailer(retailer: LocationRetailer) {
        return this.http.put<APIResponseWrapper<LocationRetailer>>(
            `${this.restApiUrl}location-endpoint/retailers/${retailer.id}`, retailer
        );
    }


    createLocation(location: Location) {
        return this.http.post<APIResponseWrapper<Location>>(
            `${this.restApiUrl}location-endpoint/location`,
            this.toLocationPayload(location)
        )
    }

    updateLocation(location: Location) {
        return this.http.put<APIResponseWrapper<Location>>(
            `${this.restApiUrl}location-endpoint/location`,
            this.toLocationPayload(location)
        );
    }

    updateDefaultContact(locationId: number, contactId: number | null) {
        const suffix = contactId == null ? '' : `?contactId=${contactId}`;
        return this.http.patch<APIResponseWrapper<Location>>(
            `${this.restApiUrl}location-endpoint/location/${locationId}/default-contact${suffix}`,
            {}
        );
    }

    getAllComboData(nameSearch: string, page: number = 0, pageSize: number = 100, cultureCode: string = 'en-US', managedById?: number) {
        const companyId = managedById ?? this.sessionStorageService.getCompany()?.id;
        if (companyId == null) {
            return of({
                hasMore: false,
                limit: 0,
                count: 0,
                offset: 0,
                items: []
            } as APIListWrapper<Location>);
        }

        const params = { nameSearch, managedById: companyId, page, pageSize, cultureCode };
        const key = JSON.stringify(params);
        const inFlight = this.comboRequests.get(key);
        if (inFlight) return inFlight;
        const request = this.http.get<APIListWrapper<Location>>(
            `${this.restApiUrl}location-endpoint/locations-combo-data`,
            { params }
        ).pipe(
            finalize(() => this.comboRequests.delete(key)),
            shareReplay({ bufferSize: 1, refCount: true })
        );
        this.comboRequests.set(key, request);
        return request;
    }

    private toLocationPayload(location: Location): Partial<Location> {
        return {
            ...location,
            defaultContact: location.defaultContact ? this.toCustomerReference(location.defaultContact) as any : location.defaultContact,
            contacts: location.contacts?.map(contact => this.toCustomerReference(contact) as any) ?? [],
            merchandisers: location.merchandisers?.map(merchandiser => this.toMerchandiserReference(merchandiser) as any) ?? [],
            merchandiserAssignments: location.merchandiserAssignments?.map(assignment => ({
                id: assignment.id,
                crmClientId: assignment.crmClientId ?? assignment.crmClient?.id,
                crmClient: assignment.crmClient ? this.toClientReference(assignment.crmClient) as any : assignment.crmClient,
                crmAccountId: assignment.crmAccountId ?? assignment.crmAccount?.id,
                crmAccount: assignment.crmAccount ? this.toAccountReference(assignment.crmAccount) as any : assignment.crmAccount,
                merchandiserId: assignment.merchandiserId ?? assignment.merchandiser?.id,
                merchandiser: assignment.merchandiser ? this.toMerchandiserReference(assignment.merchandiser) as any : assignment.merchandiser,
                accessRole: assignment.accessRole,
                primaryMerchandiser: assignment.primaryMerchandiser,
                active: assignment.active
            }) as any) ?? []
        };
    }

    private toCustomerReference(customer: any): any {
        const { roles, permissions, authority, ...customerReference } = customer;
        return customerReference;
    }

    private toMerchandiserReference(merchandiser: any): any {
        return {
            id: merchandiser?.id,
            name: merchandiser?.name,
            merchandiserNumber: merchandiser?.merchandiserNumber,
            companyId: merchandiser?.companyId
        };
    }

    private toClientReference(client: any): any {
        return {
            id: client?.id,
            clientNumber: client?.clientNumber,
            companyName: client?.companyName,
            companyId: client?.companyId
        };
    }

    private toAccountReference(account: any): any {
        return {
            id: account?.id,
            companyName: account?.companyName,
            crmClientId: account?.crmClientId,
            companyId: account?.companyId
        };
    }

}
